import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@praxis/component-auth'
import { useAnalytics } from '@praxis/component-analytics'
import {
  Table,
  Form,
  Layout,
  Grid,
  Button,
  Spinner,
  Tooltip,
  Dialog,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, {
  DownloadIcon,
  SaveIcon,
  TrashIcon,
  CautionIcon,
  HelpIcon,
} from '@enterprise-ui/icons'
import { DatePicker } from '@enterprise-ui/canvas-ui-react-datepicker'
import {
  TOASTER_DEFAULTS,
  DATA_TABLE_FIELDS,
  DATA_TABLE_ERROR_SUMMARY_FIELDS,
  LOCATION_LEVEL_MODAL_TABLE_FIELDS,
  COST_SEARCH_VIEW_BY_OPTIONS,
  COST_LOCATION_GROUP_VALUES,
  DATA_TABLE_CONDITIONAL_FIELDS,
  CUSTOM_ANALYTICS_TYPES,
  CUSTOM_ANALYTICS_EVENT_NAMES,
  getThresholdWarningText,
  noCasepackExplanationText,
  getInvalidCostErrorText,
  getNewCostThresholdWarningText,
} from '../globalConfig/constants'
import { submitCosts } from '../services/requests'
import {
  isAValidCostChange,
  newCostExceedsCostRangeThresholds,
  newCostExceedsCostThresholds,
} from '../services/helperFunctions'
import { VendorContext } from '../globalState/Vendor/index'
import { ErrorSummaryModal } from './ErrorSummaryModal'
import { LocationLevelTableModal } from './LocationLevelTableModal'
import { CostRangeExplanationModal } from './CostRangeExplanationModal'
import { StartDateTimezoneMismatchExplanationModal } from './StartDateTimezoneMismatchExplanationModal'
import _ from 'lodash'

export const DataTable = (props) => {
  const {
    tableData,
    loading,
    onDownload,
    waitingOnDownload,
    onSaveViewDelete,
    disableSaveButton,
    sendChangeCount,
  } = props
  const auth = useAuth()
  const { session } = auth
  const { trackCustomEvent } = useAnalytics()
  const [columnDefs, setColumnDefs] = useState(null)
  const [rowData, setRowData] = useState(null)
  const [waitingOnRowDataConstruction, setWaitingOnRowDataConstruction] =
    useState(false)
  const [changeCount, setChangeCount] = useState(0)
  const [errorSummary, setErrorSummary] = useState(null)
  const [waitingOnSubmitCosts, setWaitingOnSubmitCosts] = useState(false)
  const [locationLevelRowData, setLocationLevelRowData] = useState(null)
  const [locationLevelCostAdditions, setLocationLevelCostAdditions] =
    useState(null)
  const [submissionWarningDetails, setSubmissionWarningDetails] = useState(null)
  const [isCostRangeModalVisible, setIsCostRangeModalVisible] = useState(null)
  const [isTimezoneMismatchModalVisible, setIsTimezoneMismatchModalVisible] =
    useState(null)
  const [, setLastUsedStartDate] = useState(null)
  const [state] = React.useContext(VendorContext)
  const startDateRefs = useRef([])
  const endDateRefs = useRef([])
  const makeToast = useToaster()

  useEffect(() => {
    if (tableData) {
      constructColumnDefs()
      setWaitingOnRowDataConstruction(true)
    } else {
      setColumnDefs(null)
      setRowData(null)
      setLocationLevelRowData(null)
      setLocationLevelCostAdditions(null)
      setChangeCount(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData])

  useEffect(() => {
    if (waitingOnRowDataConstruction) {
      startRowDataConstruction(tableData?.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingOnRowDataConstruction])

  useEffect(() => {
    if (rowData) {
      let count = 0
      rowData.forEach((row) => {
        if (isAValidCostChange(row)) {
          count++
        }
      })
      if (locationLevelCostAdditions) {
        Object.values(locationLevelCostAdditions).forEach(
          (locationTableData) => {
            count = count + Object.keys(locationTableData)?.length
          },
        )
      }
      setChangeCount(count)
    }
  }, [rowData, locationLevelCostAdditions])

  useEffect(() => {
    if (sendChangeCount) {
      sendChangeCount(changeCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeCount])

  const roundNumber = (value, precision) => {
    var multiplier = Math.pow(10, precision || 0)
    return Math.round(value * multiplier) / multiplier
  }

  useEffect(() => {
    if (submissionWarningDetails?.exceedsThresholdDetails?.length) {
      trackCustomEvent(
        CUSTOM_ANALYTICS_TYPES.PROMPT_SHOWN,
        session?.userInfo?.lanId,
        String(submissionWarningDetails?.exceedsThresholdDetails),
        CUSTOM_ANALYTICS_EVENT_NAMES.COST_SUBMISSION_THRESHOLD_WARNING,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionWarningDetails])

  const getUnitCostInput = (
    e,
    newValue,
    rowIndex,
    casepackQuantity,
    oldCost,
    startDate,
  ) => {
    return (
      <span className="hesp-general-flex">
        <Form.Field
          className="hesp-dataTable-inputColumn"
          id={`${rowIndex}_new_unit_cost`}
          data-testid={`${rowIndex}_new_unit_cost`}
          onChange={(event) => {
            if (
              event?.target?.value.split('.')?.[1]?.length > 6 ||
              event?.nativeEvent?.data?.toUpperCase() === 'E' ||
              event?.target?.value < 0
            ) {
              return
            }
            const numberInput = event?.target?.value
              ? Number(event.target.value)
              : ''
            setLastUsedStartDate((prevStartDate) => {
              setRowData((prevRowData) => {
                const newArray = Array.from(prevRowData)
                newArray[rowIndex].new_unit_cost.cellValue =
                  numberInput || numberInput === 0 ? numberInput : ''
                newArray[rowIndex].new_unit_cost.cellDisplay = getUnitCostInput(
                  event,
                  numberInput || numberInput === 0 ? numberInput : '',
                  rowIndex,
                  casepackQuantity,
                  oldCost,
                  prevRowData[rowIndex]?.new_start_date?.cellValue,
                )
                newArray[rowIndex].new_casepack_cost.cellValue =
                  numberInput || numberInput === 0
                    ? numberInput * casepackQuantity
                    : ''
                newArray[rowIndex].new_casepack_cost.cellDisplay =
                  getCasepackCostInput(
                    event,
                    numberInput || numberInput === 0
                      ? numberInput * casepackQuantity
                      : '',
                    rowIndex,
                    casepackQuantity,
                    oldCost,
                    startDate,
                  )
                newArray[rowIndex].new_start_date.cellDisplay =
                  getStartDateInput(
                    prevRowData[rowIndex].new_start_date.cellValue ||
                      prevStartDate,
                    rowIndex,
                    prevRowData[rowIndex].new_end_date.cellValue,
                    numberInput || numberInput === 0 ? numberInput : '',
                  )
                return newArray
              })
              return prevStartDate
            })
          }}
          value={newValue || newValue === 0 ? roundNumber(newValue, 6) : ''}
          highlight={newCostExceedsCostRangeThresholds(oldCost, newValue)}
          error={(startDate && !newValue) || newValue === 0}
          type="number"
          step="any"
          placeholder="$"
          onWheel={(e) => e.target.blur()}
        />
        {newCostExceedsCostRangeThresholds(oldCost, newValue) ? (
          <Tooltip
            className="hesp-general-wordBreak-keepAll hesp-general-flex"
            content={
              _.isEmpty(oldCost)
                ? getNewCostThresholdWarningText()
                : getThresholdWarningText()
            }
            location="bottom"
          >
            <Button
              className="hesp-dataTable-thresholdAlert"
              data-testid={`${rowIndex}-newUnitCost-thresholdWarning`}
              iconOnly
              type="ghost"
              aria-label="Threshold Warning Icon"
            >
              <EnterpriseIcon icon={CautionIcon} />
            </Button>
          </Tooltip>
        ) : null}
        {newValue === 0 ? (
          <Tooltip
            className="hesp-general-wordBreak-keepAll hesp-general-flex"
            content={getInvalidCostErrorText()}
            location="bottom"
          >
            <Button
              className="hesp-dataTable-invalidCostError"
              data-testid={`${rowIndex}-newUnitCost-invalidCostError`}
              iconOnly
              type="ghost"
              aria-label="Invalid Cost Error Icon"
            >
              <EnterpriseIcon icon={CautionIcon} />
            </Button>
          </Tooltip>
        ) : null}
      </span>
    )
  }

  const getCasepackCostInput = (
    e,
    newValue,
    rowIndex,
    casepackQuantity,
    oldCost,
    startDate,
  ) => {
    if (casepackQuantity) {
      return (
        <span className="hesp-general-flex">
          <Form.Field
            className="hesp-dataTable-inputColumn"
            id={`${rowIndex}_new_casepack_cost`}
            data-testid={`${rowIndex}_new_casepack_cost`}
            onChange={(event) => {
              if (
                event?.target?.value.split('.')?.[1]?.length > 6 ||
                event?.nativeEvent?.data?.toUpperCase() === 'E' ||
                event?.target?.value < 0
              ) {
                return
              }
              const numberInput = event?.target?.value
                ? Number(event.target.value)
                : ''
              setLastUsedStartDate((prevStartDate) => {
                setRowData((prevRowData) => {
                  const newArray = Array.from(prevRowData)
                  newArray[rowIndex].new_casepack_cost.cellValue =
                    numberInput || numberInput === 0 ? numberInput : ''
                  newArray[rowIndex].new_casepack_cost.cellDisplay =
                    getCasepackCostInput(
                      event,
                      numberInput || numberInput === 0 ? numberInput : '',
                      rowIndex,
                      casepackQuantity,
                      oldCost,
                      startDate,
                    )
                  newArray[rowIndex].new_unit_cost.cellValue =
                    numberInput || numberInput === 0
                      ? numberInput / casepackQuantity
                      : ''
                  newArray[rowIndex].new_unit_cost.cellDisplay =
                    getUnitCostInput(
                      event,
                      numberInput || numberInput === 0
                        ? numberInput / casepackQuantity
                        : '',
                      rowIndex,
                      casepackQuantity,
                      oldCost,
                      startDate,
                    )
                  newArray[rowIndex].new_start_date.cellDisplay =
                    getStartDateInput(
                      prevRowData[rowIndex].new_start_date.cellValue ||
                        prevStartDate,
                      rowIndex,
                      prevRowData[rowIndex].new_end_date.cellValue,
                      numberInput || numberInput === 0
                        ? numberInput / casepackQuantity
                        : '',
                    )
                  return newArray
                })
                return prevStartDate
              })
            }}
            value={newValue || newValue === 0 ? roundNumber(newValue, 6) : ''}
            highlight={newCostExceedsCostRangeThresholds(
              oldCost,
              newValue || newValue === 0 ? newValue / casepackQuantity : '',
            )}
            error={(startDate && !newValue) || newValue === 0}
            type="number"
            step="any"
            placeholder="$"
            onWheel={(e) => e.target.blur()}
          />
          {newCostExceedsCostRangeThresholds(
            oldCost,
            newValue || newValue === 0 ? newValue / casepackQuantity : '',
          ) ? (
            <Tooltip
              className="hesp-general-wordBreak-keepAll hesp-general-flex"
              content={
                _.isEmpty(oldCost)
                  ? getNewCostThresholdWarningText()
                  : getThresholdWarningText()
              }
              location="bottom"
            >
              <Button
                className="hesp-dataTable-thresholdAlert"
                data-testid={`${rowIndex}-newCasepackCost-thresholdWarning`}
                iconOnly
                type="ghost"
                aria-label="Threshold Warning Icon"
              >
                <EnterpriseIcon icon={CautionIcon} />
              </Button>
            </Tooltip>
          ) : null}
          {newValue === 0 ? (
            <Tooltip
              className="hesp-general-wordBreak-keepAll hesp-general-flex"
              content={getInvalidCostErrorText()}
              location="bottom"
            >
              <Button
                className="hesp-dataTable-invalidCostError"
                data-testid={`${rowIndex}-newUnitCost-invalidCostError`}
                iconOnly
                type="ghost"
                aria-label="Invalid Cost Error Icon"
              >
                <EnterpriseIcon icon={CautionIcon} />
              </Button>
            </Tooltip>
          ) : null}
        </span>
      )
    } else {
      return (
        <Tooltip
          className="hesp-general-wordBreak-keepAll hesp-general-block"
          content={noCasepackExplanationText}
          location="bottom"
        >
          <div className="hesp-general-ellipsedText">N/A</div>
        </Tooltip>
      )
    }
  }

  const getStartDateInput = (newValue, rowIndex, endDate, unitCost) => {
    return (
      <span
        onClick={() => {
          setTimeout(() => {
            startDateRefs?.current?.[rowIndex]?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }, 100)
        }}
        ref={startDateRefs?.current?.[rowIndex]}
      >
        <DatePicker
          className="hesp-dataTable-inputColumn"
          id={`${rowIndex}_new_start_date`}
          key={`${rowIndex}_new_start_date`}
          data-testid={`${rowIndex}_new_start_date`}
          location="bottom-left"
          error={unitCost && !newValue}
          onUpdate={(id, value) => {
            setLastUsedStartDate(value)
            setRowData((prevRowData) => {
              const newArray = Array.from(prevRowData)
              newArray[rowIndex].new_start_date.cellValue = value
              newArray[rowIndex].new_start_date.cellDisplay = getStartDateInput(
                value,
                rowIndex,
                endDate,
                prevRowData[rowIndex]?.new_unit_cost.cellValue,
              )
              newArray[rowIndex].new_end_date.cellDisplay = getEndDateInput(
                endDate,
                rowIndex,
                value,
              )
              newArray[rowIndex].new_unit_cost.cellDisplay = getUnitCostInput(
                null,
                prevRowData[rowIndex].new_unit_cost.cellValue,
                rowIndex,
                prevRowData[rowIndex]?.[
                  DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName
                ]?.cellValue,
                prevRowData[rowIndex]?.[
                  DATA_TABLE_FIELDS?.COST_RANGE?.variableName
                ]?.cellValue,
                value,
              )
              newArray[rowIndex].new_casepack_cost.cellDisplay =
                getCasepackCostInput(
                  null,
                  prevRowData[rowIndex].new_casepack_cost.cellValue,
                  rowIndex,
                  prevRowData[rowIndex]?.[
                    DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName
                  ]?.cellValue,
                  prevRowData[rowIndex]?.[
                    DATA_TABLE_FIELDS?.COST_RANGE?.variableName
                  ]?.cellValue,
                  value,
                )
              return newArray
            })
          }}
          disableDates={{
            before: new Date().toLocaleDateString('en-US', { timeZone: 'UTC' }),
            after: endDate,
          }}
          value={newValue}
        />
      </span>
    )
  }

  const getEndDateInput = (newValue, rowIndex, startDate) => {
    return (
      <span
        onClick={() => {
          setTimeout(() => {
            endDateRefs?.current?.[rowIndex]?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }, 100)
        }}
        ref={endDateRefs?.current?.[rowIndex]}
      >
        <DatePicker
          className="hesp-dataTable-inputColumn hesp-dataTable-inputColumn-endDate"
          id={`${rowIndex}_new_end_date`}
          key={`${rowIndex}_new_end_date`}
          data-testid={`${rowIndex}_new_end_date`}
          location="bottom-left"
          onUpdate={(id, value) => {
            setRowData((prevRowData) => {
              const newArray = Array.from(prevRowData)
              newArray[rowIndex].new_end_date.cellValue = value
              newArray[rowIndex].new_end_date.cellDisplay = getEndDateInput(
                value,
                rowIndex,
                startDate,
              )
              newArray[rowIndex].new_start_date.cellDisplay = getStartDateInput(
                startDate,
                rowIndex,
                value,
              )
              return newArray
            })
          }}
          disableDates={{
            before:
              startDate ||
              new Date().toLocaleDateString('en-US', { timeZone: 'UTC' }),
          }}
          value={newValue}
          placeholder="12/31/9999"
        />
      </span>
    )
  }

  const constructColumnDefs = () => {
    const newColumnDefs = []
    const allHeaderVariableNames = Object?.values(DATA_TABLE_FIELDS).reduce(
      (self, header) => ({ ...self, [header.variableName]: header }),
      {},
    )
    Object.keys(allHeaderVariableNames).forEach((rowDataVariable) => {
      newColumnDefs.push({
        field: rowDataVariable,
        headerName:
          Object.keys(DATA_TABLE_FIELDS).includes(
            'COST_LOCATION_GROUP_VALUE',
          ) &&
          rowDataVariable ===
            DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE?.variableName ? (
            tableData?.selections?.costLocationGroup?.[
              LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                .variableName
            ] === COST_SEARCH_VIEW_BY_OPTIONS?.CHAIN?.variableName ? (
              'View By'
            ) : (
              COST_SEARCH_VIEW_BY_OPTIONS?.[
                tableData?.selections?.costLocationGroup?.[
                  LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                    .variableName
                ]
              ]?.label
            )
          ) : rowDataVariable === DATA_TABLE_FIELDS.COST_RANGE.variableName ? (
            <>
              {allHeaderVariableNames?.[rowDataVariable]?.label}{' '}
              <EnterpriseIcon
                style={{ paddingRight: '5px' }}
                icon={HelpIcon}
                size="inline"
                onClick={() => {
                  setIsCostRangeModalVisible(true)
                }}
              />
            </>
          ) : rowDataVariable ===
              DATA_TABLE_FIELDS?.NEW_START_DATE_INPUT?.variableName &&
            new Date().toLocaleDateString('en-US', { timeZone: 'UTC' }) !==
              new Date().toLocaleDateString('en-US') ? (
            <span className="hesp-general-flex hesp-general-justifyCenter">
              {allHeaderVariableNames?.[rowDataVariable]?.label}{' '}
              <Button
                className="hesp-dataTable-thresholdAlert"
                data-testid="startDate-timezone-mismatch-warning"
                iconOnly
                type="ghost"
                aria-label="Timezone Mismatch Warning Icon"
                onClick={() => {
                  setIsTimezoneMismatchModalVisible(true)
                }}
              >
                <EnterpriseIcon icon={CautionIcon} />
              </Button>
            </span>
          ) : (
            allHeaderVariableNames?.[rowDataVariable]?.label
          ),
        width:
          allHeaderVariableNames?.[rowDataVariable]?.tableDetails
            ?.defaultColumnWidth,
        align: allHeaderVariableNames?.[rowDataVariable]?.tableDetails?.align,
        isRowHeader: rowDataVariable === DATA_TABLE_FIELDS.TCIN.variableName,
      })
    })
    const newSortedColumnDefs = Object.keys(allHeaderVariableNames)
      .filter(function (i) {
        return Object.keys(allHeaderVariableNames).includes(i)
      })
      .map((i) => newColumnDefs.find((j) => j?.field === i))
    setColumnDefs(injectConditionalColumns(newSortedColumnDefs))
  }

  const injectConditionalColumns = (sortedColumnDefs) => {
    const returnArray = _.cloneDeep(sortedColumnDefs)
    if (
      [COST_SEARCH_VIEW_BY_OPTIONS?.CUSTOM?.variableName].includes(
        tableData?.selections?.costLocationGroup?.[
          LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
            .variableName
        ],
      )
    ) {
      returnArray.splice(3, 0, {
        field:
          DATA_TABLE_CONDITIONAL_FIELDS?.COST_LOCATION_GROUP_NAME?.variableName,
        headerName:
          COST_SEARCH_VIEW_BY_OPTIONS?.[
            tableData?.selections?.costLocationGroup?.[
              LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                .variableName
            ]
          ]?.label + ' Name',
        width:
          DATA_TABLE_CONDITIONAL_FIELDS?.COST_LOCATION_GROUP_NAME?.tableDetails
            ?.defaultColumnWidth,
        align:
          DATA_TABLE_CONDITIONAL_FIELDS?.COST_LOCATION_GROUP_NAME?.tableDetails
            ?.align,
        isRowHeader: false,
      })
    }

    return returnArray
  }

  const getConditionalFields = () => {
    const returnObj = {}
    if (
      [COST_SEARCH_VIEW_BY_OPTIONS?.CUSTOM?.variableName].includes(
        tableData?.selections?.costLocationGroup?.[
          LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
            .variableName
        ],
      )
    ) {
      returnObj[
        DATA_TABLE_CONDITIONAL_FIELDS.COST_LOCATION_GROUP_NAME.keyName
      ] = DATA_TABLE_CONDITIONAL_FIELDS.COST_LOCATION_GROUP_NAME
    }

    return returnObj
  }

  const startRowDataConstruction = (data) => {
    constructRowData(data)
      .then((constructedData) => {
        setRowData(constructedData)
        setLocationLevelCostAdditions(null)
        setWaitingOnRowDataConstruction(false)
      })
      .catch(() => {
        setWaitingOnRowDataConstruction(false)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: `Error`,
          message: `We encountered an unexpected error while putting your results into our table.`,
        })
      })
  }

  const constructRowData = (data) => {
    return new Promise(function (resolve, reject) {
      const newRowData = []
      const allFieldVariableNames = Object.values({
        ...DATA_TABLE_FIELDS,
        ...getConditionalFields(),
      }).reduce((self, field) => ({ ...self, [field.variableName]: field }), {})
      data.forEach((dataRow, rowIndex) => {
        startDateRefs.current.push(React.createRef())
        endDateRefs.current.push(React.createRef())
        const formattedRow = {}
        formattedRow[
          LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE.variableName
        ] =
          dataRow?.[
            LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE.variableName
          ]
        formattedRow.costIds = dataRow?.costIds

        formattedRow.new_start_date = {
          cellValue: '',
          cellDisplay: getStartDateInput('', rowIndex),
        }
        formattedRow.new_end_date = {
          cellValue: '',
          cellDisplay: getEndDateInput('', rowIndex),
        }
        formattedRow.new_unit_cost = {
          cellValue: '',
          cellDisplay: getUnitCostInput(
            '',
            '',
            rowIndex,
            dataRow[DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName],
            dataRow[DATA_TABLE_FIELDS?.COST_RANGE?.variableName],
          ),
        }
        formattedRow.new_casepack_cost = {
          cellValue: '',
          cellDisplay: getCasepackCostInput(
            '',
            '',
            rowIndex,
            dataRow[DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName],
            dataRow[DATA_TABLE_FIELDS?.COST_RANGE?.variableName],
          ),
        }
        Object.keys(allFieldVariableNames).forEach((dataRowKey) => {
          if (
            ![
              DATA_TABLE_FIELDS?.NEW_START_DATE_INPUT?.variableName,
              DATA_TABLE_FIELDS?.NEW_END_DATE_INPUT?.variableName,
              DATA_TABLE_FIELDS?.NEW_UNIT_COST_INPUT?.variableName,
              DATA_TABLE_FIELDS?.NEW_CASEPACK_COST_INPUT?.variableName,
            ].includes(dataRowKey)
          ) {
            const isLinkableCell =
              dataRowKey === DATA_TABLE_FIELDS?.COST_RANGE?.variableName &&
              dataRow[dataRowKey] &&
              dataRow[dataRowKey]?.minCost &&
              dataRow[dataRowKey]?.maxCost &&
              dataRow?.[
                LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                  .variableName
              ] !== COST_SEARCH_VIEW_BY_OPTIONS.LOCATION.variableName
            const linkableCellClass = ' hesp-dataTable-costCell'
            formattedRow[dataRowKey] = {
              cellValue: dataRow[dataRowKey],
              cellDisplay: (
                <div
                  className={`${
                    allFieldVariableNames[dataRowKey]?.tableDetails
                      ?.cellClassName
                  }${isLinkableCell ? linkableCellClass : ''}`}
                  onClick={
                    isLinkableCell
                      ? () => {
                          setLocationLevelRowData({
                            ...dataRow,
                            index: rowIndex,
                          })
                        }
                      : null
                  }
                >
                  {allFieldVariableNames[dataRowKey]?.tableDetails?.toStringFunc
                    ? allFieldVariableNames[
                        dataRowKey
                      ]?.tableDetails?.toStringFunc(dataRow[dataRowKey])
                    : dataRow[dataRowKey]}
                </div>
              ),
            }
          }
        })
        newRowData.push(formattedRow)
      })
      resolve(newRowData)
    })
  }

  const clearInputValues = () => {
    const rowDataCopy = Array.from(rowData)
    rowDataCopy.forEach((row, rowIndex) => {
      if (row.new_start_date.cellValue || row.new_unit_cost.cellValue) {
        rowDataCopy[rowIndex].new_start_date.cellValue = ''
        rowDataCopy[rowIndex].new_start_date.cellDisplay = getStartDateInput(
          '',
          rowIndex,
        )
        rowDataCopy[rowIndex].new_unit_cost.cellValue = ''
        rowDataCopy[rowIndex].new_unit_cost.cellDisplay = getUnitCostInput(
          '',
          '',
          rowIndex,
          row?.[DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName]?.cellValue,
          row[DATA_TABLE_FIELDS?.COST_RANGE?.variableName].cellValue,
        )
      }
      if (row.new_end_date.cellValue) {
        rowDataCopy[rowIndex].new_end_date.cellValue = ''
        rowDataCopy[rowIndex].new_end_date.cellDisplay = getEndDateInput(
          '',
          rowIndex,
        )
      }
      if (
        row.new_casepack_cost.cellValue ||
        row.new_casepack_cost.cellValue === 0
      ) {
        rowDataCopy[rowIndex].new_casepack_cost.cellValue = ''
        rowDataCopy[rowIndex].new_casepack_cost.cellDisplay =
          getCasepackCostInput(
            '',
            '',
            rowIndex,
            row?.[DATA_TABLE_FIELDS?.CASEPACK_QUANTITY?.variableName]
              ?.cellValue,
            row[DATA_TABLE_FIELDS?.COST_RANGE?.variableName].cellValue,
          )
      }
    })
    setRowData(rowDataCopy)
    setLocationLevelCostAdditions(null)
  }

  const getSubmissionWarningDetails = () => {
    let partialCount = 0
    let exceedsThresholdCount = 0
    const exceedsThresholdDetails = []
    rowData.forEach((row) => {
      const hasCost = Boolean(row?.new_unit_cost?.cellValue)
      const hasStartDate =
        row?.new_start_date?.cellValue || row?.new_start_date?.cellValue === 0
      if (hasCost !== hasStartDate && !isAValidCostChange(row)) {
        partialCount++
      }
      if (
        hasCost &&
        newCostExceedsCostRangeThresholds(
          row?.[DATA_TABLE_FIELDS?.COST_RANGE?.variableName]?.cellValue,
          row?.new_unit_cost?.cellValue,
        )
      ) {
        exceedsThresholdCount++
        exceedsThresholdDetails.push(
          `${row?.[DATA_TABLE_FIELDS?.TCIN?.variableName]?.cellValue}-${
            row?.[
              LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                .variableName
            ]
          }-${row?.[DATA_TABLE_FIELDS?.COST_LOCATION_GROUP_VALUE?.variableName]?.cellValue}: ${row?.new_unit_cost?.cellValue}`,
        )
      }
    })
    if (locationLevelCostAdditions) {
      Object.values(locationLevelCostAdditions).forEach(
        (singleRowLocationLevelChanges) => {
          Object.values(singleRowLocationLevelChanges).forEach(
            (locationLevelData) => {
              if (
                newCostExceedsCostThresholds(
                  locationLevelData.previousCost,
                  locationLevelData.cost,
                )
              ) {
                exceedsThresholdCount++
                exceedsThresholdDetails.push(
                  `${locationLevelData?.[DATA_TABLE_FIELDS?.TCIN?.variableName]}-${
                    locationLevelData?.costLocationGroup?.[
                      LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                        .variableName
                    ]
                  }-${locationLevelData?.costLocationGroup?.[LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_VALUES.variableName]}: ${locationLevelData.cost}`,
                )
              }
            },
          )
        },
      )
    }
    return partialCount || exceedsThresholdCount
      ? {
          partialCount: partialCount,
          exceedsThresholdCount: exceedsThresholdCount,
          exceedsThresholdDetails: exceedsThresholdDetails,
        }
      : null
  }

  const getSubmissionWarningHeadingText = () => {
    if (
      submissionWarningDetails?.partialCount &&
      submissionWarningDetails?.exceedsThresholdCount
    ) {
      return 'Warnings'
    } else if (submissionWarningDetails?.partialCount) {
      return 'You Have Partially Completed Cost Submissions'
    } else {
      return 'You Have Costs that Exceed The Warning Threshold'
    }
  }

  const getSubmissionWarningBodyText = () => {
    const partialText = submissionWarningDetails?.partialCount
      ? `You have ${submissionWarningDetails?.partialCount} partially completed
         row${submissionWarningDetails?.partialCount > 1 ? 's' : ''} where you've entered
         a New Start Date or a New Cost, but not both, which is required
         for a valid cost submission.  If you proceed with submitting the completed rows,
         these partially completed rows will be ignored and any values you
         entered will be removed.`
      : ''
    const exceedsThresholdText = submissionWarningDetails?.exceedsThresholdCount
      ? `You have ${submissionWarningDetails?.exceedsThresholdCount}
         cost${submissionWarningDetails?.exceedsThresholdCount > 1 ? 's' : ''}
         that exceed the warning threshold.  Please verify that you've entered
         the correct
         cost${submissionWarningDetails?.exceedsThresholdCount > 1 ? 's' : ''}
         for ${submissionWarningDetails?.exceedsThresholdCount > 1 ? 'those rows' : 'that row'}.`
      : ''
    const lineBreak =
      partialText && exceedsThresholdText ? [<br />, <br />] : ''
    return [partialText, ...lineBreak, exceedsThresholdText]
  }

  const makeSubmitCostsRequest = (bypassSubmissionWarningDialog) => {
    if (!bypassSubmissionWarningDialog) {
      const warningDetails = getSubmissionWarningDetails()
      if (warningDetails) {
        setSubmissionWarningDetails(warningDetails)
        return
      }
    }
    setWaitingOnSubmitCosts(true)
    const requestBody = { source: 'UI', costChanges: [] }
    rowData.forEach((row) => {
      if (isAValidCostChange(row)) {
        requestBody.costChanges.push({
          [LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName]: roundNumber(
            row.new_unit_cost.cellValue,
            6,
          ),
          startDate: new Date(row.new_start_date.cellValue).toLocaleDateString(
            'fr-CA',
          ),
          endDate: row.new_end_date.cellValue
            ? new Date(row.new_end_date.cellValue).toLocaleDateString('fr-CA')
            : '9999-12-31',
          costLocationGroup: {
            [LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
              .variableName]:
              row?.[
                LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                  .variableName
              ],
            [COST_LOCATION_GROUP_VALUES.variableName]:
              row?.[DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE.variableName]
                .cellValue === COST_SEARCH_VIEW_BY_OPTIONS.CHAIN.variableName
                ? []
                : [
                    row?.[
                      DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE.variableName
                    ].cellValue,
                  ],
          },
          [DATA_TABLE_FIELDS?.TCIN?.variableName]:
            row?.[DATA_TABLE_FIELDS?.TCIN?.variableName]?.cellValue,
          vendorIds: [state.selectedVendor],
        })
      }
    })
    if (locationLevelCostAdditions) {
      Object.values(locationLevelCostAdditions).forEach((locationTableData) => {
        Object.values(locationTableData).forEach((row) => {
          delete row.previousCost
          requestBody.costChanges.push(row)
        })
      })
    }
    submitCosts(requestBody)
      .then((response) => {
        setWaitingOnSubmitCosts(false)
        if (response?.status === 200) {
          if (response?.data?.length) {
            if (changeCount === response?.data?.length) {
              makeToast({
                ...TOASTER_DEFAULTS,
                type: 'alert',
                heading: 'Warning',
                message: `All of your attempted cost updates encountered errors.`,
              })
              setErrorSummary(response?.data)
            } else {
              makeToast({
                ...TOASTER_DEFAULTS,
                type: 'alert',
                heading: 'Warning',
                message: (
                  <div>
                    <p className="hc-mb-normal hesp-black-text">
                      {response?.data?.length} of your {changeCount} attempted
                      cost updates encountered errors.
                    </p>
                    <p className="hesp-black-text">
                      Cost changes will not be valid until they've been approved
                      by a Target team member. Therefore changes may not be
                      immediatley reflected in the system.
                    </p>
                  </div>
                ),
              })
              setErrorSummary(response?.data)
            }
          } else {
            makeToast({
              ...TOASTER_DEFAULTS,
              type: 'success',
              heading:
                changeCount > 1
                  ? `${changeCount} Cost Updates Submitted`
                  : `${changeCount} Cost Update Submitted`,
              message: (
                <div>
                  <p className="hc-mb-normal hesp-white-text">
                    {changeCount > 1
                      ? `All ${changeCount} cost updates were successfully submitted.`
                      : `${changeCount} cost update successfully submitted.`}
                  </p>
                  <p className="hesp-white-text">
                    Cost changes will not be valid until they've been approved
                    by a Target team member. Therefore changes may not be
                    immediatley reflected in the system.
                  </p>
                </div>
              ),
            })
            clearInputValues()
          }
        } else {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'error',
            heading: `Error`,
            message: `We encountered an error and failed to submit your cost updates.`,
          })
        }
      })
      .catch(() => {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: `Error`,
          message: `We encountered an error and failed to submit your cost updates.`,
        })
        setWaitingOnSubmitCosts(false)
      })
  }

  const handleLocationLevelAddition = (costChanges, rowIndex) => {
    setLocationLevelCostAdditions((prevLocationLevelCostAdditions) => {
      return {
        ...prevLocationLevelCostAdditions,
        [rowIndex]: costChanges,
      }
    })
    setLocationLevelRowData(null)
  }

  return (
    <>
      {rowData && columnDefs ? (
        <Layout.Body
          className="hesp-costSearch-tableLayout"
          data-testid="cost-search-footer"
        >
          <Grid.Container className="hc-pa-normal">
            <Grid.Item className="hesp-general-fullwidth hesp-position-relative hc-pa-none hesp-cost-search-table-safari">
              {loading || waitingOnRowDataConstruction ? (
                <>
                  <span
                    data-testid="history-table-upload-lockout"
                    className="hesp-lockout-container hesp-lockout-overlay"
                  ></span>
                  <span className={'hesp-lockout-container'}>
                    <Spinner data-testid="data-table-spinner" size="expanded" />
                  </span>
                </>
              ) : null}
              <div className="hesp-general-flex hc-mb-normal">
                {onSaveViewDelete && (
                  <Button
                    className="hesp-general-ghostButton"
                    data-testid="delete-view-table-button"
                    type="ghost"
                    onClick={() => props.onSaveViewDelete()}
                  >
                    <EnterpriseIcon className="hc-mr-dense" icon={TrashIcon} />
                    DELETE SAVED VIEW
                  </Button>
                )}
                {onDownload &&
                  tableData?.selections?.costLocationGroup?.[
                    LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
                      .variableName
                  ] !== COST_SEARCH_VIEW_BY_OPTIONS.LOCATION.variableName && (
                    <Button
                      className="hc-mr-normal hesp-general-ghostButton"
                      style={{ width: '140px' }}
                      data-testid="download-search-results"
                      type="ghost"
                      onClick={onDownload}
                      isLoading={waitingOnDownload}
                      disabled={rowData?.length === 0}
                    >
                      {waitingOnDownload ? null : (
                        <>
                          <EnterpriseIcon
                            className="hc-mr-dense"
                            icon={DownloadIcon}
                          />
                          DOWNLOAD
                        </>
                      )}
                    </Button>
                  )}
                <Button
                  data-testid="saved-view-table-button"
                  type="secondary"
                  onClick={() => props.onSaveViewOpen()}
                  disabled={disableSaveButton}
                >
                  <EnterpriseIcon className="hc-mr-dense" icon={SaveIcon} />
                  SAVE VIEW
                </Button>
                <Button
                  data-testid="submit-costs-button"
                  className="hesp-general-marginLeft"
                  type="primary"
                  disabled={changeCount < 1}
                  isLoading={waitingOnSubmitCosts}
                  onClick={() => makeSubmitCostsRequest()}
                >
                  {`SUBMIT ${changeCount} COST UPDATE${
                    changeCount === 1 ? '' : 'S'
                  }`}
                </Button>
              </div>
              <Table
                className={`hesp-table-header hesp-cost-search-table${!rowData?.length ? ' hesp-table-empty' : ''}`}
                data-testid="cost-search-table"
                name="Cost Search Data Table"
                id="cost-search-table"
                cellPadding="dense"
                height={500}
                fixedHeader
                data={{
                  defaultColDef: { sortable: false, resizable: true },
                  columnDefs: columnDefs,
                  rowData: rowData?.length ? rowData : [{}],
                }}
                roundCorner
                pagination={{
                  type: 'client',
                  pageSizeOptions: [10, 25, 50, 100],
                  initialPageSize: 50,
                }}
                scrollableWidth
                alternateRowColor
              />
              {rowData.length === 0 ? (
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '100%',
                    textAlign: 'center',
                    bottom: '325px',
                  }}
                >
                  {'No results found'}
                </span>
              ) : null}
              <ErrorSummaryModal
                isVisible={Boolean(errorSummary)}
                tableData={errorSummary}
                headerMetadata={DATA_TABLE_ERROR_SUMMARY_FIELDS}
                rowHeader={DATA_TABLE_ERROR_SUMMARY_FIELDS.TCIN}
                headingText="Cost Update Error Summary"
                subtext="We encountered errors and were unable to make the following updates:"
                onClose={() => {
                  setErrorSummary(null)
                }}
                scrollableWidth={false}
              />
              <LocationLevelTableModal
                isVisible={Boolean(locationLevelRowData)}
                onRefuse={() => {
                  setLocationLevelRowData(null)
                }}
                onSubmit={handleLocationLevelAddition}
                parentRowData={locationLevelRowData}
                existingSelections={
                  locationLevelCostAdditions?.[locationLevelRowData?.index]
                }
              />
            </Grid.Item>
          </Grid.Container>
        </Layout.Body>
      ) : loading || waitingOnRowDataConstruction ? (
        <Layout.Body
          className="hesp-costSearch-tableLayout"
          data-testid="cost-search-footer"
        >
          <Grid.Container className="hc-pa-normal">
            <Grid.Item
              className="hesp-general-fullwidth hesp-position-relative hc-pa-none"
              style={{ height: '500px' }}
            >
              <span
                data-testid="data-table-upload-lockout"
                className="hesp-lockout-container hesp-lockout-overlay"
              ></span>
              <span className={'hesp-lockout-container'}>
                <Spinner data-testid="data-table-spinner" size="expanded" />
              </span>
            </Grid.Item>
          </Grid.Container>
        </Layout.Body>
      ) : null}
      <Dialog
        headingText={getSubmissionWarningHeadingText()}
        isVisible={Boolean(submissionWarningDetails)}
        refuseButtonText="Cancel"
        bodyText={getSubmissionWarningBodyText()}
        onRefuse={() => {
          setSubmissionWarningDetails(null)
        }}
        approveButtonText="Proceed With Cost Submissions"
        onApprove={() => {
          setSubmissionWarningDetails(null)
          makeSubmitCostsRequest(true)
        }}
      />
      <CostRangeExplanationModal
        isVisible={isCostRangeModalVisible}
        onRefuse={() => {
          setIsCostRangeModalVisible(false)
        }}
        fieldLabel={DATA_TABLE_FIELDS?.COST_RANGE?.label}
      />
      <StartDateTimezoneMismatchExplanationModal
        isVisible={isTimezoneMismatchModalVisible}
        onRefuse={() => {
          setIsTimezoneMismatchModalVisible(false)
        }}
      />
    </>
  )
}
